/**
 * A tree of conditions, as the thing editing them sees it.
 *
 * Three places in this application store `field op value` combined by one
 * connective and nested in groups: a view's filters, a form question's
 * conditions, and a colour's. This draws the tree for any of them, and the
 * differences stop at the ids -- a view's groups are rows the database numbers,
 * and a colour's live inside one JSON column and are named by whoever wrote
 * them.
 *
 * The tree itself comes from `buildFilterTree`, the same function that decides
 * whether a row matches, so the shape somebody is editing and the shape the
 * rows are chosen by cannot disagree.
 */
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import type { TreeNode } from '../rows/rowMatch'
import { ConditionRow, ConnectiveCell, type Condition } from './condition-row'
import type { ConditionChanges } from './condition-row'

/** What names one condition or one group, whichever kind the owner hands out. */
export type ConditionId = number | string

export interface ConditionTreeActions<T extends Condition> {
  onConnective: (node: TreeNode<T>, next: 'AND' | 'OR') => void
  onAddFilter: (group: ConditionId | null) => void
  onAddGroup: (parentGroup: ConditionId | null) => void
  onChange: (filterId: ConditionId, changes: ConditionChanges) => void
  onRemove: (filterId: ConditionId) => void
  onRemoveGroup: (groupId: ConditionId) => void
}

/**
 * The conditions directly inside one node, and the groups nested in it.
 *
 * Conditions first and then groups, in the order each was made. They are two
 * sequences rather than one -- a filter id and a group id come from different
 * tables -- so interleaving them by id would put a group between two conditions
 * for no reason anybody could see.
 *
 * Recursive, because the backend's nesting is: a group holds groups. Nothing
 * caps the depth here, for the same reason nothing caps it in `buildFilterTree`
 * -- the rule is the same at every level, and a limit invented here would be a
 * shape this application could store and not show.
 */
export function ConditionsNode<T extends Condition>({
  node,
  label,
  fields,
  context,
  onConnective,
  onAddFilter,
  onAddGroup,
  onChange,
  onRemove,
  onRemoveGroup,
}: {
  node: TreeNode<T>
  label: string
  fields: readonly Field[]
  context: FieldContext
} & ConditionTreeActions<T>) {
  const { t } = useTranslation()

  return (
    <ul aria-label={label} className="flex flex-col gap-1">
      {node.filters.map((filter, index) => (
        <ConditionRow
          key={filter.id}
          filter={filter}
          position={index}
          connective={node.conjunction}
          fields={fields}
          context={context}
          onConnective={(next) => {
            onConnective(node, next)
          }}
          onChange={(changes) => {
            onChange(filter.id, changes)
          }}
          onRemove={() => {
            onRemove(filter.id)
          }}
        />
      ))}

      {node.children.map((child, index) => (
        <li key={child.id} className="flex items-start gap-2">
          {/* The group's place among what the *parent* combines, which is why
              the position counts the conditions before it. */}
          <ConnectiveCell
            position={node.filters.length + index}
            connective={node.conjunction}
            onConnective={(next) => {
              onConnective(node, next)
            }}
          />
          {/* A region rather than a plain box: the two buttons inside it read
              "add a filter" and "add a group" exactly as the ones outside do,
              and what tells them apart is what they are inside. */}
          <div
            role="group"
            aria-label={t('view.filterGroup')}
            className="flex-1 rounded border p-2"
          >
            {child.filters.length === 0 && child.children.length === 0 ? (
              <p className="px-1 py-0.5 text-xs text-muted-foreground">
                {t('view.filterGroupEmpty')}
              </p>
            ) : (
              <ConditionsNode
                node={child}
                label={t('view.filterGroup')}
                fields={fields}
                context={context}
                onConnective={onConnective}
                onAddFilter={onAddFilter}
                onAddGroup={onAddGroup}
                onChange={onChange}
                onRemove={onRemove}
                onRemoveGroup={onRemoveGroup}
              />
            )}
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-muted"
                onClick={() => {
                  if (child.id !== null) onAddFilter(child.id)
                }}
              >
                {t('view.filterAdd')}
              </button>
              <button
                type="button"
                className="rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-muted"
                onClick={() => {
                  if (child.id !== null) onAddGroup(child.id)
                }}
              >
                {t('view.filterAddGroup')}
              </button>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('view.filterRemoveGroup')}
            // Never null: only the root has no id, and the root is not drawn as
            // a group -- it is the list these are in.
            onClick={() => {
              if (child.id !== null) onRemoveGroup(child.id)
            }}
            className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="size-3.5" />
          </button>
        </li>
      ))}
    </ul>
  )
}
